import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './MediaGallery.css';

const MediaGallery = ({ media }) => {
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const allMedia = [
    ...media.images.map(img => ({ ...img, type: 'image' })),
    ...media.videos.map(vid => ({ ...vid, type: 'video' }))
  ];

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedMedia(allMedia[index]);
  };

  const closeLightbox = () => {
    setSelectedMedia(null);
  };

  const nextMedia = () => {
    const nextIndex = (currentIndex + 1) % allMedia.length;
    setCurrentIndex(nextIndex);
    setSelectedMedia(allMedia[nextIndex]);
  };

  const prevMedia = () => {
    const prevIndex = (currentIndex - 1 + allMedia.length) % allMedia.length;
    setCurrentIndex(prevIndex);
    setSelectedMedia(allMedia[prevIndex]);
  };

  if (allMedia.length === 0) return null;

  return (
    <div className="media-gallery">
      <div className="media-grid">
        {media.images.map((image, index) => (
          <motion.div
            key={`img-${index}`}
            className="media-item image-item"
            onClick={() => openLightbox(index)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <img 
              src={image.src} 
              alt={image.alt || 'Experience image'} 
              loading="lazy"
            />
            <div className="media-overlay">
              <i className="fas fa-search-plus"></i>
              {image.caption && <p>{image.caption}</p>}
            </div>
          </motion.div>
        ))}

        {media.videos.map((video, videoIndex) => {
          const index = media.images.length + videoIndex;
          return (
            <motion.div
              key={`vid-${videoIndex}`}
              className="media-item video-item"
              onClick={() => openLightbox(index)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="video-thumbnail">
                {video.thumbnail ? (
                  <img 
                    src={video.thumbnail} 
                    alt={video.title || 'Video thumbnail'} 
                    loading="lazy"
                  />
                ) : (
                  <div className="video-placeholder">
                    <i className="fas fa-play"></i>
                  </div>
                )}
                <div className="play-button">
                  <i className="fas fa-play"></i>
                </div>
              </div>
              <div className="media-overlay">
                <i className="fas fa-play"></i>
                {video.title && <p>{video.title}</p>}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedMedia && (
          <motion.div
            className="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
          >
            <motion.div
              className="lightbox-content"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="lightbox-close" onClick={closeLightbox}>
                <i className="fas fa-times"></i>
              </button>

              {allMedia.length > 1 && (
                <>
                  <button className="lightbox-nav prev" onClick={prevMedia}>
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <button className="lightbox-nav next" onClick={nextMedia}>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </>
              )}

              <div className="lightbox-media">
                {selectedMedia.type === 'image' ? (
                  <img 
                    src={selectedMedia.src} 
                    alt={selectedMedia.alt || 'Experience image'} 
                  />
                ) : (
                  <video 
                    src={selectedMedia.src} 
                    controls 
                    autoPlay
                    poster={selectedMedia.thumbnail}
                  >
                    Your browser does not support the video tag.
                  </video>
                )}
              </div>

              <div className="lightbox-info">
                {selectedMedia.caption && (
                  <p className="lightbox-caption">{selectedMedia.caption}</p>
                )}
                {selectedMedia.title && (
                  <h4 className="lightbox-title">{selectedMedia.title}</h4>
                )}
                {selectedMedia.description && (
                  <p className="lightbox-description">{selectedMedia.description}</p>
                )}
              </div>

              {allMedia.length > 1 && (
                <div className="lightbox-counter">
                  {currentIndex + 1} / {allMedia.length}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MediaGallery;
